import { useCallback, useEffect, useRef, type RefObject } from 'react'
import {
  FLAVOUR_PHASE_EVENT,
  isFlavourPhase,
  type FlavourPhase,
  type FlavourPhaseDetail,
} from '../utils/flavourPhase'
import {
  SFX_LOOP_VISIBILITY_EVENT,
  SFX_ONE_SHOT_EVENT,
  SFX_SIGNAL_PULSE_EVENT,
  SFX_SIGNAL_VISIBILITY_EVENT,
  type SfxLoopVisibilityDetail,
  type SfxOneShotDetail,
  type SfxSignalPulseDetail,
  type SfxSignalVisibilityDetail,
} from '../utils/sfxEvents'

export {
  SFX_LOOP_VISIBILITY_EVENT,
  SFX_ONE_SHOT_EVENT,
  SFX_SIGNAL_PULSE_EVENT,
  SFX_SIGNAL_VISIBILITY_EVENT,
  type SfxLoopVisibilityDetail,
  type SfxOneShotDetail,
  type SfxSignalPulseDetail,
  type SfxSignalVisibilityDetail,
} from '../utils/sfxEvents'

/** Editable loudness and timing controls for Nova's audio layer. */
export const SFX_AUDIO_TUNING = {
  // Global gain for every Nova sound effect.
  masterVolume: 0.56,
  ambientLoops: {
    'deep-space': {
      src: `${import.meta.env.BASE_URL}media/sounds/deepspace.mp3`,
      volume: 0.56,
      fadeInMs: 1_400,
      fadeOutMs: 2_300,
    },
    'ancient-spark': {
      src: `${import.meta.env.BASE_URL}media/sounds/ancient%20spark.mp3`,
      volume: 1,
      fadeInMs: 1_400,
      fadeOutMs: 2_300,
    },
    'event-horizon': {
      src: `${import.meta.env.BASE_URL}media/sounds/black%20hole.mp3`,
      volume: 0.26,
      fadeInMs: 1_500,
      fadeOutMs: 2_300,
    },
  },
  crossfadeDurationMs: 2_400,
  visibilityFadeInMs: 700,
  loopToggleFadeOutMs: 420,
  loopSeamFadeOutMs: 6_000,
  loopSeamFadeInMs: 6_000,
  signalWaves: {
    // Kept intentionally quiet: this sits underneath the flavour ambience.
    outputVolume: 0.16,
    pulseVolume: 0.42,
    attackMs: 190,
    tailMs: 2_350,
    startFrequencyHz: 58,
    endFrequencyHz: 42,
    overtoneRatio: 1.62,
    overtoneVolume: 0.16,
    toggleFadeOutMs: 420,
  },
} as const

type AmbientLoopPhase = keyof typeof SFX_AUDIO_TUNING.ambientLoops

type ResolvedLoopVisibilityDetail = {
  id: string
  src: string
  visible: boolean
  volume: number
  fadeInMs: number
  fadeOutMs: number
}

type MediaLoopLayer = {
  id: string
  src: string
  audio: HTMLAudioElement
  level: number
  volume: number
  frame: number | null
  generation: number
  failed: boolean
}

type SignalVoice = {
  dispose: () => void
}

type SignalAudioGraph = {
  context: AudioContext
  output: GainNode
  voices: Set<SignalVoice>
}

const clampVolume = (volume: number) => Math.min(Math.max(volume, 0), 1)

const safeVolume = (volume: number | undefined, fallback: number) =>
  Number.isFinite(volume) ? clampVolume(volume as number) : fallback

const safePlaybackRate = (playbackRate: number | undefined) =>
  Number.isFinite(playbackRate) ? Math.max(playbackRate as number, 0.1) : 1

const safeDuration = (durationMs: number | undefined) =>
  Number.isFinite(durationMs)
    ? Math.min(Math.max(durationMs as number, 0), 30_000)
    : 0

const safeSignalIntensity = (intensity: number | undefined) =>
  Number.isFinite(intensity) ? clampVolume(intensity as number) : 1

const holdAudioParam = (parameter: AudioParam, atTime: number) => {
  try {
    parameter.cancelAndHoldAtTime(atTime)
  } catch {
    const currentValue = parameter.value
    parameter.cancelScheduledValues(atTime)
    parameter.setValueAtTime(currentValue, atTime)
  }
}

const easeInOutSine = (progress: number) =>
  -(Math.cos(Math.PI * progress) - 1) / 2

const getLoopSeamGain = (audio: HTMLAudioElement) => {
  const currentTime = Math.max(audio.currentTime, 0)
  const rawFadeInSeconds = SFX_AUDIO_TUNING.loopSeamFadeInMs / 1_000
  const rawFadeOutSeconds = SFX_AUDIO_TUNING.loopSeamFadeOutMs / 1_000
  const duration = audio.duration

  let fadeInSeconds = rawFadeInSeconds
  let fadeOutSeconds = rawFadeOutSeconds
  if (Number.isFinite(duration) && duration > 0) {
    const maximumEnvelopeTime = duration * 0.9
    const requestedEnvelopeTime = fadeInSeconds + fadeOutSeconds
    if (requestedEnvelopeTime > maximumEnvelopeTime) {
      const scale = maximumEnvelopeTime / requestedEnvelopeTime
      fadeInSeconds *= scale
      fadeOutSeconds *= scale
    }
  }

  const fadeInGain = easeInOutSine(
    Math.min(currentTime / Math.max(fadeInSeconds, 0.001), 1),
  )
  if (!Number.isFinite(duration) || duration <= 0) return fadeInGain

  const remainingTime = Math.max(duration - currentTime, 0)
  const fadeOutGain = easeInOutSine(
    Math.min(remainingTime / Math.max(fadeOutSeconds, 0.001), 1),
  )
  return Math.min(fadeInGain, fadeOutGain)
}

const getAmbientLoopPhase = (
  phase: FlavourPhase,
): AmbientLoopPhase | null => {
  if (
    phase === 'deep-space' ||
    phase === 'ancient-spark' ||
    phase === 'event-horizon'
  ) {
    return phase
  }
  return null
}

/**
 * Owns Nova's ambient, looping and one-shot audio lifecycle.
 *
 * `enabled` must be controlled by an explicit user-facing toggle. No media is
 * created or played while it is false, keeping browser autoplay behaviour
 * predictable.
 */
export function useSfxAudio<T extends HTMLElement>(
  root: RefObject<T | null>,
  enabled: boolean,
) {
  const enabledRef = useRef(enabled)
  const currentPhaseRef = useRef<FlavourPhase>('before')
  const ambientLoopsRef = useRef(
    new Map<AmbientLoopPhase, HTMLAudioElement>(),
  )
  const oneShotsRef = useRef(
    new Map<HTMLAudioElement, () => void>(),
  )
  const mediaLoopLayersRef = useRef(new Map<string, MediaLoopLayer>())
  const mediaLoopVisibilityRef = useRef(
    new Map<string, ResolvedLoopVisibilityDetail>(),
  )
  const mixLevelsRef = useRef(new Map<AmbientLoopPhase, number>())
  const mixFrameRef = useRef<number | null>(null)
  const seamFrameRef = useRef<number | null>(null)
  const targetAmbientPhaseRef = useRef<AmbientLoopPhase | null>(null)
  const transitionGenerationRef = useRef(0)
  const signalVisibilityRef = useRef<SfxSignalVisibilityDetail>({
    visible: false,
    durationMs: 0,
  })
  const latestSignalPulseRef = useRef<SfxSignalPulseDetail>({ intensity: 0 })
  const signalAudioGraphRef = useRef<SignalAudioGraph | null>(null)
  const signalSuspendTimerRef = useRef<number | null>(null)

  const clearSignalSuspendTimer = useCallback(() => {
    if (signalSuspendTimerRef.current === null) return
    window.clearTimeout(signalSuspendTimerRef.current)
    signalSuspendTimerRef.current = null
  }, [])

  const getSignalAudioGraph = useCallback(() => {
    const existingGraph = signalAudioGraphRef.current
    if (existingGraph) return existingGraph
    if (!enabledRef.current || typeof window.AudioContext === 'undefined') {
      return null
    }

    const context = new window.AudioContext()
    const output = context.createGain()
    output.gain.value = 0
    output.connect(context.destination)

    const graph: SignalAudioGraph = {
      context,
      output,
      voices: new Set(),
    }
    signalAudioGraphRef.current = graph
    return graph
  }, [])

  const setSignalOutputGain = useCallback(
    (
      graph: SignalAudioGraph,
      visible: boolean,
      durationMs: number,
      suspendAfterFade = !visible,
    ) => {
      clearSignalSuspendTimer()

      const duration = safeDuration(durationMs)
      const now = graph.context.currentTime
      const targetGain = visible
        ? SFX_AUDIO_TUNING.masterVolume *
          SFX_AUDIO_TUNING.signalWaves.outputVolume
        : 0

      holdAudioParam(graph.output.gain, now)
      if (duration <= 0) {
        graph.output.gain.setValueAtTime(targetGain, now)
      } else {
        graph.output.gain.linearRampToValueAtTime(
          targetGain,
          now + duration / 1_000,
        )
      }

      if (!visible && suspendAfterFade) {
        signalSuspendTimerRef.current = window.setTimeout(() => {
          signalSuspendTimerRef.current = null
          if (signalAudioGraphRef.current !== graph) return

          graph.voices.forEach((voice) => voice.dispose())
          if (graph.context.state === 'running') {
            void graph.context.suspend().catch(() => undefined)
          }
        }, duration + 50)
      }
    },
    [clearSignalSuspendTimer],
  )

  const applySignalVisibility = useCallback(
    (detail: SfxSignalVisibilityDetail) => {
      if (!enabledRef.current || document.hidden) return

      if (!detail.visible) {
        const graph = signalAudioGraphRef.current
        if (graph) {
          setSignalOutputGain(graph, false, detail.durationMs)
        }
        return
      }

      const graph = getSignalAudioGraph()
      if (!graph || graph.context.state === 'closed') return

      setSignalOutputGain(graph, true, detail.durationMs, false)
      if (graph.context.state === 'suspended') {
        void graph.context.resume().catch(() => undefined)
      }
    },
    [getSignalAudioGraph, setSignalOutputGain],
  )

  const playSignalPulse = useCallback(
    (detail: SfxSignalPulseDetail) => {
      if (
        !enabledRef.current ||
        document.hidden ||
        !signalVisibilityRef.current.visible
      ) {
        return
      }

      const graph = getSignalAudioGraph()
      if (!graph || graph.context.state === 'closed') return

      if (graph.context.state === 'suspended') {
        void graph.context.resume().catch(() => undefined)
      }

      const tuning = SFX_AUDIO_TUNING.signalWaves
      const intensity = safeSignalIntensity(detail.intensity)
      const attackSeconds = tuning.attackMs / 1_000
      const tailSeconds = tuning.tailMs / 1_000
      const now = graph.context.currentTime + 0.012
      const releaseAt = now + attackSeconds + tailSeconds

      const fundamental = graph.context.createOscillator()
      const overtone = graph.context.createOscillator()
      const fundamentalLevel = graph.context.createGain()
      const overtoneLevel = graph.context.createGain()
      const envelope = graph.context.createGain()

      fundamental.type = 'sine'
      fundamental.frequency.setValueAtTime(tuning.startFrequencyHz, now)
      fundamental.frequency.exponentialRampToValueAtTime(
        tuning.endFrequencyHz,
        releaseAt,
      )
      overtone.type = 'sine'
      overtone.frequency.setValueAtTime(
        tuning.startFrequencyHz * tuning.overtoneRatio,
        now,
      )
      overtone.frequency.exponentialRampToValueAtTime(
        tuning.endFrequencyHz * tuning.overtoneRatio,
        releaseAt,
      )

      fundamentalLevel.gain.value = 1
      overtoneLevel.gain.value = tuning.overtoneVolume
      envelope.gain.setValueAtTime(0.0001, now)
      envelope.gain.exponentialRampToValueAtTime(
        Math.max(tuning.pulseVolume * intensity, 0.0001),
        now + attackSeconds,
      )
      envelope.gain.exponentialRampToValueAtTime(0.0001, releaseAt)

      fundamental.connect(fundamentalLevel)
      overtone.connect(overtoneLevel)
      fundamentalLevel.connect(envelope)
      overtoneLevel.connect(envelope)
      envelope.connect(graph.output)

      let disposed = false
      const voice: SignalVoice = {
        dispose: () => {
          if (disposed) return
          disposed = true
          fundamental.removeEventListener('ended', voice.dispose)
          try {
            fundamental.stop()
          } catch {
            // The oscillator has already reached its scheduled stop.
          }
          try {
            overtone.stop()
          } catch {
            // The oscillator has already reached its scheduled stop.
          }
          fundamental.disconnect()
          overtone.disconnect()
          fundamentalLevel.disconnect()
          overtoneLevel.disconnect()
          envelope.disconnect()
          graph.voices.delete(voice)
        },
      }

      graph.voices.add(voice)
      fundamental.addEventListener('ended', voice.dispose, { once: true })
      fundamental.start(now)
      overtone.start(now)
      fundamental.stop(releaseAt + 0.04)
      overtone.stop(releaseAt + 0.04)
    },
    [getSignalAudioGraph],
  )

  const disposeSignalAudio = useCallback(() => {
    clearSignalSuspendTimer()
    const graph = signalAudioGraphRef.current
    signalAudioGraphRef.current = null
    if (!graph) return

    graph.voices.forEach((voice) => voice.dispose())
    graph.output.disconnect()
    if (graph.context.state !== 'closed') {
      void graph.context.close().catch(() => undefined)
    }
  }, [clearSignalSuspendTimer])

  const cancelMixAnimation = useCallback(() => {
    if (mixFrameRef.current !== null) {
      window.cancelAnimationFrame(mixFrameRef.current)
      mixFrameRef.current = null
    }
  }, [])

  const cancelSeamRenderer = useCallback(() => {
    if (seamFrameRef.current !== null) {
      window.cancelAnimationFrame(seamFrameRef.current)
      seamFrameRef.current = null
    }
  }, [])

  const setMediaLoopLevel = useCallback(
    (layer: MediaLoopLayer, level: number) => {
      layer.level = clampVolume(level)
      layer.audio.volume = clampVolume(
        SFX_AUDIO_TUNING.masterVolume * layer.volume * layer.level *
          getLoopSeamGain(layer.audio),
      )
    },
    [],
  )

  const disposeMediaLoopLayer = useCallback((id: string) => {
    const layer = mediaLoopLayersRef.current.get(id)
    if (!layer) return

    layer.generation += 1
    if (layer.frame !== null) {
      window.cancelAnimationFrame(layer.frame)
      layer.frame = null
    }
    layer.audio.volume = 0
    layer.audio.pause()
    layer.audio.removeAttribute('src')
    layer.audio.load()
    mediaLoopLayersRef.current.delete(id)
  }, [])

  const getMediaLoopLayer = useCallback(
    (detail: ResolvedLoopVisibilityDetail) => {
      const existingLayer = mediaLoopLayersRef.current.get(detail.id)
      if (existingLayer?.src === detail.src) return existingLayer
      if (existingLayer) disposeMediaLoopLayer(detail.id)
      if (typeof Audio === 'undefined') return null

      const audio = new Audio(detail.src)
      audio.loop = true
      audio.preload = 'auto'
      audio.volume = 0
      const layer: MediaLoopLayer = {
        id: detail.id,
        src: detail.src,
        audio,
        level: 0,
        volume: detail.volume,
        frame: null,
        generation: 0,
        failed: false,
      }
      mediaLoopLayersRef.current.set(detail.id, layer)
      return layer
    },
    [disposeMediaLoopLayer],
  )

  const animateMediaLoopLayer = useCallback(
    (layer: MediaLoopLayer, visible: boolean, durationMs: number) => {
      layer.generation += 1
      const generation = layer.generation
      if (layer.frame !== null) {
        window.cancelAnimationFrame(layer.frame)
        layer.frame = null
      }

      const beginFade = () => {
        if (generation !== layer.generation) return
        const initialLevel = layer.level
        const targetLevel = visible ? 1 : 0
        const duration = safeDuration(durationMs)

        const finish = () => {
          setMediaLoopLevel(layer, targetLevel)
          layer.frame = null
          if (!visible) {
            layer.audio.pause()
            layer.audio.currentTime = 0
          } else {
            // Keep the same soft loop boundary used by the flavour ambience,
            // even after the section's entrance fade has finished.
            const updateLoopEnvelope = () => {
              if (generation !== layer.generation) return
              setMediaLoopLevel(layer, layer.level)
              layer.frame = null
              if (!layer.audio.paused && !document.hidden) {
                layer.frame = window.requestAnimationFrame(updateLoopEnvelope)
              }
            }
            layer.frame = window.requestAnimationFrame(updateLoopEnvelope)
          }
        }

        if (duration <= 0 || Math.abs(targetLevel - initialLevel) < 0.0001) {
          finish()
          return
        }

        const startedAt = performance.now()
        const update = (now: number) => {
          if (generation !== layer.generation) return
          const progress = Math.min((now - startedAt) / duration, 1)
          const easedProgress = easeInOutSine(progress)
          setMediaLoopLevel(
            layer,
            initialLevel + (targetLevel - initialLevel) * easedProgress,
          )
          if (progress < 1) {
            layer.frame = window.requestAnimationFrame(update)
            return
          }
          finish()
        }

        layer.frame = window.requestAnimationFrame(update)
      }

      if (!visible) {
        beginFade()
        return
      }

      const requested = mediaLoopVisibilityRef.current.get(layer.id)
      if (
        !enabledRef.current ||
        document.hidden ||
        layer.failed ||
        !requested?.visible ||
        requested.src !== layer.src
      ) {
        return
      }

      void layer.audio.play().then(() => {
        const latestRequest = mediaLoopVisibilityRef.current.get(layer.id)
        if (
          generation !== layer.generation ||
          !enabledRef.current ||
          document.hidden ||
          !latestRequest?.visible ||
          latestRequest.src !== layer.src
        ) {
          if (layer.level <= 0.0001) {
            layer.audio.pause()
            layer.audio.currentTime = 0
          }
          return
        }
        beginFade()
      }).catch(() => {
        if (generation !== layer.generation) return
        layer.failed = true
        setMediaLoopLevel(layer, 0)
        layer.audio.pause()
      })
    },
    [setMediaLoopLevel],
  )

  const applyMediaLoopVisibility = useCallback(
    (
      detail: ResolvedLoopVisibilityDetail,
      durationOverride?: number,
    ) => {
      if (!detail.visible) {
        const existingLayer = mediaLoopLayersRef.current.get(detail.id)
        if (!existingLayer) return
        existingLayer.volume = detail.volume
        animateMediaLoopLayer(
          existingLayer,
          false,
          durationOverride ?? detail.fadeOutMs,
        )
        return
      }

      if (!enabledRef.current || document.hidden) return
      const layer = getMediaLoopLayer(detail)
      if (!layer || layer.failed) return
      layer.volume = detail.volume
      animateMediaLoopLayer(
        layer,
        true,
        durationOverride ?? detail.fadeInMs,
      )
    },
    [animateMediaLoopLayer, getMediaLoopLayer],
  )

  const silenceMediaLoops = useCallback(
    (durationMs: number) => {
      mediaLoopLayersRef.current.forEach((layer) => {
        animateMediaLoopLayer(layer, false, durationMs)
      })
    },
    [animateMediaLoopLayer],
  )

  const pauseMediaLoopsImmediately = useCallback(() => {
    mediaLoopLayersRef.current.forEach((layer) => {
      layer.generation += 1
      if (layer.frame !== null) {
        window.cancelAnimationFrame(layer.frame)
        layer.frame = null
      }
      layer.level = 0
      layer.audio.volume = 0
      layer.audio.pause()
      layer.audio.currentTime = 0
    })
  }, [])

  const resumeVisibleMediaLoops = useCallback(
    (durationOverride?: number) => {
      mediaLoopVisibilityRef.current.forEach((detail) => {
        if (detail.visible) {
          applyMediaLoopVisibility(detail, durationOverride)
        }
      })
    },
    [applyMediaLoopVisibility],
  )

  const getAmbientLoop = useCallback((phase: AmbientLoopPhase) => {
    const existingAudio = ambientLoopsRef.current.get(phase)
    if (existingAudio) return existingAudio
    if (typeof Audio === 'undefined') return null

    const config = SFX_AUDIO_TUNING.ambientLoops[phase]
    const audio = new Audio(config.src)
    audio.loop = true
    audio.preload = 'auto'
    audio.volume = 0
    ambientLoopsRef.current.set(phase, audio)
    mixLevelsRef.current.set(phase, 0)
    return audio
  }, [])

  const ensureSeamRenderer = useCallback(() => {
    if (seamFrameRef.current !== null || document.hidden) return

    const render = () => {
      seamFrameRef.current = null

      ambientLoopsRef.current.forEach((audio, phase) => {
        const mixLevel = mixLevelsRef.current.get(phase) ?? 0
        if (mixLevel <= 0.0001 && phase !== targetAmbientPhaseRef.current) {
          audio.volume = 0
          if (!audio.paused) {
            audio.pause()
            audio.currentTime = 0
          }
          return
        }

        const config = SFX_AUDIO_TUNING.ambientLoops[phase]
        const seamGain = getLoopSeamGain(audio)
        audio.volume = clampVolume(
          SFX_AUDIO_TUNING.masterVolume *
            config.volume *
            mixLevel *
            seamGain,
        )
      })

      const shouldContinue =
        targetAmbientPhaseRef.current !== null ||
        mixFrameRef.current !== null ||
        Array.from(mixLevelsRef.current.values()).some(
          (mixLevel) => mixLevel > 0.0001,
        )
      if (!document.hidden && shouldContinue) {
        seamFrameRef.current = window.requestAnimationFrame(render)
      }
    }

    seamFrameRef.current = window.requestAnimationFrame(render)
  }, [])

  const stopOneShots = useCallback(() => {
    const disposers = Array.from(oneShotsRef.current.values())
    disposers.forEach((dispose) => dispose())
  }, [])

  const animateAmbientMix = useCallback(
    (
      targetPhase: AmbientLoopPhase | null,
      generation: number,
      durationOverride?: number,
    ) => {
      cancelMixAnimation()
      const loops = Array.from(ambientLoopsRef.current.entries())
      if (loops.length === 0) return

      const initialMixLevels = new Map(
        loops.map(([phase]) => [phase, mixLevelsRef.current.get(phase) ?? 0]),
      )
      const hasAudibleOutgoingLoop = loops.some(
        ([phase]) =>
          phase !== targetPhase &&
          (mixLevelsRef.current.get(phase) ?? 0) > 0.001,
      )
      const duration =
        durationOverride ??
        (targetPhase
          ? hasAudibleOutgoingLoop
            ? SFX_AUDIO_TUNING.crossfadeDurationMs
            : SFX_AUDIO_TUNING.ambientLoops[targetPhase].fadeInMs
          : Math.max(
              ...loops.map(
                ([phase]) => SFX_AUDIO_TUNING.ambientLoops[phase].fadeOutMs,
              ),
            ))
      const startedAt = performance.now()
      ensureSeamRenderer()

      const update = (now: number) => {
        if (generation !== transitionGenerationRef.current) return

        const progress = Math.min((now - startedAt) / Math.max(duration, 1), 1)
        const easedProgress = easeInOutSine(progress)
        const incomingProgress = Math.sin((progress * Math.PI) / 2)
        const outgoingProgress = Math.cos((progress * Math.PI) / 2)

        loops.forEach(([phase]) => {
          const initialMixLevel = initialMixLevels.get(phase) ?? 0
          if (phase === targetPhase) {
            const blend = hasAudibleOutgoingLoop
              ? incomingProgress
              : easedProgress
            mixLevelsRef.current.set(
              phase,
              initialMixLevel + (1 - initialMixLevel) * blend,
            )
            return
          }

          const blend = hasAudibleOutgoingLoop
            ? outgoingProgress
            : 1 - easedProgress
          mixLevelsRef.current.set(phase, initialMixLevel * blend)
        })

        if (progress < 1) {
          mixFrameRef.current = window.requestAnimationFrame(update)
          return
        }

        mixFrameRef.current = null
        loops.forEach(([phase]) => {
          if (phase === targetPhase) {
            mixLevelsRef.current.set(phase, 1)
            return
          }
          mixLevelsRef.current.set(phase, 0)
        })
      }

      mixFrameRef.current = window.requestAnimationFrame(update)
    },
    [cancelMixAnimation, ensureSeamRenderer],
  )

  const transitionAmbientMix = useCallback(
    (targetPhase: AmbientLoopPhase | null, durationOverride?: number) => {
      const generation = transitionGenerationRef.current + 1
      transitionGenerationRef.current = generation
      cancelMixAnimation()
      targetAmbientPhaseRef.current = targetPhase

      if (!targetPhase) {
        animateAmbientMix(null, generation, durationOverride)
        return
      }

      const targetAudio = getAmbientLoop(targetPhase)
      if (!targetAudio) {
        targetAmbientPhaseRef.current = null
        return
      }
      ensureSeamRenderer()

      const beginMix = () => {
        if (
          generation !== transitionGenerationRef.current ||
          !enabledRef.current ||
          getAmbientLoopPhase(currentPhaseRef.current) !== targetPhase ||
          document.hidden
        ) {
          if (
            targetAmbientPhaseRef.current !== targetPhase &&
            (mixLevelsRef.current.get(targetPhase) ?? 0) <= 0.0001
          ) {
            targetAudio.pause()
            targetAudio.currentTime = 0
          }
          return
        }
        animateAmbientMix(targetPhase, generation, durationOverride)
      }

      void targetAudio.play().then(beginMix).catch(() => {
        if (generation !== transitionGenerationRef.current) return
        if (targetAmbientPhaseRef.current === targetPhase) {
          targetAmbientPhaseRef.current = null
        }
        targetAudio.pause()
        targetAudio.currentTime = 0
        animateAmbientMix(null, generation)
      })
    },
    [
      animateAmbientMix,
      cancelMixAnimation,
      ensureSeamRenderer,
      getAmbientLoop,
    ],
  )

  const primeAmbientLoops = useCallback(() => {
    const generation = transitionGenerationRef.current + 1
    transitionGenerationRef.current = generation
    cancelMixAnimation()

    const phases = Object.keys(
      SFX_AUDIO_TUNING.ambientLoops,
    ) as AmbientLoopPhase[]
    const loops = phases.flatMap((phase) => {
      const audio = getAmbientLoop(phase)
      if (!audio) return []
      return [[phase, audio] as const]
    })

    void Promise.allSettled(loops.map(([, audio]) => audio.play())).then(() => {
      if (generation !== transitionGenerationRef.current) {
        loops.forEach(([phase, audio]) => {
          if (
            phase === targetAmbientPhaseRef.current ||
            (mixLevelsRef.current.get(phase) ?? 0) > 0.0001
          ) {
            return
          }
          audio.volume = 0
          audio.pause()
          audio.currentTime = 0
        })
        return
      }

      loops.forEach(([, audio]) => {
        audio.volume = 0
        audio.pause()
        audio.currentTime = 0
      })

      const targetPhase = getAmbientLoopPhase(currentPhaseRef.current)
      if (!enabledRef.current || document.hidden || !targetPhase) {
        return
      }

      transitionAmbientMix(targetPhase)
    })
  }, [cancelMixAnimation, getAmbientLoop, transitionAmbientMix])

  const playOneShot = useCallback((detail: SfxOneShotDetail) => {
    if (
      !enabledRef.current ||
      document.hidden ||
      typeof Audio === 'undefined' ||
      !detail.src
    ) {
      return
    }

    const audio = new Audio(detail.src)
    audio.preload = 'auto'
    audio.volume = clampVolume(
      safeVolume(detail.volume, 1) * SFX_AUDIO_TUNING.masterVolume,
    )
    audio.playbackRate = safePlaybackRate(detail.playbackRate)

    const dispose = () => {
      audio.removeEventListener('ended', dispose)
      audio.removeEventListener('error', dispose)
      audio.pause()
      audio.removeAttribute('src')
      oneShotsRef.current.delete(audio)
    }

    oneShotsRef.current.set(audio, dispose)
    audio.addEventListener('ended', dispose, { once: true })
    audio.addEventListener('error', dispose, { once: true })
    void audio.play().catch(dispose)
  }, [])

  useEffect(() => {
    const rootElement = root.current
    if (!rootElement) return

    const journeyRoot =
      rootElement.querySelector<HTMLElement>('[data-can-journey]')
    const initialPhase = journeyRoot?.dataset.flavourPhase
    if (initialPhase && isFlavourPhase(initialPhase)) {
      currentPhaseRef.current = initialPhase
    }

    const handlePhaseChange = (event: Event) => {
      const detail = (event as CustomEvent<FlavourPhaseDetail>).detail
      if (!detail || !isFlavourPhase(detail.phase)) return

      currentPhaseRef.current = detail.phase
      if (!enabledRef.current) return
      transitionAmbientMix(getAmbientLoopPhase(detail.phase))
    }

    const handleOneShot = (event: Event) => {
      const detail = (event as CustomEvent<SfxOneShotDetail>).detail
      if (!detail) return
      playOneShot(detail)
    }

    const handleLoopVisibility = (event: Event) => {
      const detail = (event as CustomEvent<SfxLoopVisibilityDetail>).detail
      if (!detail || typeof detail.visible !== 'boolean') return

      const id = detail.id?.trim()
      const src = detail.src?.trim()
      if (!id || !src) return
      const previous = mediaLoopVisibilityRef.current.get(id)
      const nextVisibility: ResolvedLoopVisibilityDetail = {
        id,
        src,
        visible: detail.visible,
        volume: safeVolume(detail.volume, previous?.volume ?? 1),
        fadeInMs:
          detail.fadeInMs === undefined
            ? previous?.fadeInMs ?? SFX_AUDIO_TUNING.visibilityFadeInMs
            : safeDuration(detail.fadeInMs),
        fadeOutMs:
          detail.fadeOutMs === undefined
            ? previous?.fadeOutMs ?? SFX_AUDIO_TUNING.visibilityFadeInMs
            : safeDuration(detail.fadeOutMs),
      }
      mediaLoopVisibilityRef.current.set(id, nextVisibility)
      applyMediaLoopVisibility(nextVisibility)
    }

    const handleSignalVisibility = (event: Event) => {
      const detail = (event as CustomEvent<SfxSignalVisibilityDetail>).detail
      if (!detail || typeof detail.visible !== 'boolean') return

      const nextVisibility = {
        visible: detail.visible,
        durationMs: safeDuration(detail.durationMs),
      }
      signalVisibilityRef.current = nextVisibility
      applySignalVisibility(nextVisibility)
    }

    const handleSignalPulse = (event: Event) => {
      const detail = (event as CustomEvent<SfxSignalPulseDetail>).detail
      if (!detail) return

      const nextPulse = {
        intensity: safeSignalIntensity(detail.intensity),
      }
      latestSignalPulseRef.current = nextPulse
      playSignalPulse(nextPulse)
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        transitionGenerationRef.current += 1
        cancelMixAnimation()
        cancelSeamRenderer()
        targetAmbientPhaseRef.current = null
        ambientLoopsRef.current.forEach((audio, phase) => {
          mixLevelsRef.current.set(phase, 0)
          audio.volume = 0
          audio.pause()
          audio.currentTime = 0
        })
        pauseMediaLoopsImmediately()
        stopOneShots()

        const signalGraph = signalAudioGraphRef.current
        if (signalGraph) {
          clearSignalSuspendTimer()
          setSignalOutputGain(signalGraph, false, 0, false)
          signalGraph.voices.forEach((voice) => voice.dispose())
          if (signalGraph.context.state === 'running') {
            void signalGraph.context.suspend().catch(() => undefined)
          }
        }
        return
      }

      if (!enabledRef.current) return
      const targetPhase = getAmbientLoopPhase(currentPhaseRef.current)
      if (targetPhase) {
        transitionAmbientMix(
          targetPhase,
          SFX_AUDIO_TUNING.visibilityFadeInMs,
        )
      }
      resumeVisibleMediaLoops(SFX_AUDIO_TUNING.visibilityFadeInMs)

      if (signalVisibilityRef.current.visible) {
        applySignalVisibility({
          visible: true,
          durationMs: SFX_AUDIO_TUNING.visibilityFadeInMs,
        })
      }
    }

    rootElement.addEventListener(FLAVOUR_PHASE_EVENT, handlePhaseChange)
    rootElement.addEventListener(SFX_ONE_SHOT_EVENT, handleOneShot)
    rootElement.addEventListener(
      SFX_LOOP_VISIBILITY_EVENT,
      handleLoopVisibility,
    )
    rootElement.addEventListener(
      SFX_SIGNAL_VISIBILITY_EVENT,
      handleSignalVisibility,
    )
    rootElement.addEventListener(SFX_SIGNAL_PULSE_EVENT, handleSignalPulse)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      rootElement.removeEventListener(FLAVOUR_PHASE_EVENT, handlePhaseChange)
      rootElement.removeEventListener(SFX_ONE_SHOT_EVENT, handleOneShot)
      rootElement.removeEventListener(
        SFX_LOOP_VISIBILITY_EVENT,
        handleLoopVisibility,
      )
      rootElement.removeEventListener(
        SFX_SIGNAL_VISIBILITY_EVENT,
        handleSignalVisibility,
      )
      rootElement.removeEventListener(
        SFX_SIGNAL_PULSE_EVENT,
        handleSignalPulse,
      )
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [
    applyMediaLoopVisibility,
    applySignalVisibility,
    cancelMixAnimation,
    cancelSeamRenderer,
    clearSignalSuspendTimer,
    pauseMediaLoopsImmediately,
    playOneShot,
    playSignalPulse,
    resumeVisibleMediaLoops,
    root,
    setSignalOutputGain,
    stopOneShots,
    transitionAmbientMix,
  ])

  useEffect(() => {
    enabledRef.current = enabled

    if (!enabled) {
      transitionAmbientMix(null)
      silenceMediaLoops(SFX_AUDIO_TUNING.loopToggleFadeOutMs)
      stopOneShots()

      const signalGraph = signalAudioGraphRef.current
      if (signalGraph) {
        setSignalOutputGain(
          signalGraph,
          false,
          SFX_AUDIO_TUNING.signalWaves.toggleFadeOutMs,
        )
      }
      return
    }

    // This effect follows the explicit SFX-toggle interaction. Priming media
    // here prevents later scene events from becoming autoplay attempts.
    primeAmbientLoops()
    resumeVisibleMediaLoops(SFX_AUDIO_TUNING.visibilityFadeInMs)
    const signalGraph = getSignalAudioGraph()
    if (signalGraph && !document.hidden) {
      if (signalGraph.context.state === 'suspended') {
        void signalGraph.context.resume().catch(() => undefined)
      }
      applySignalVisibility(signalVisibilityRef.current)
    }
  }, [
    applySignalVisibility,
    enabled,
    getSignalAudioGraph,
    primeAmbientLoops,
    resumeVisibleMediaLoops,
    setSignalOutputGain,
    silenceMediaLoops,
    stopOneShots,
    transitionAmbientMix,
  ])

  useEffect(
    () => () => {
      transitionGenerationRef.current += 1
      cancelMixAnimation()
      cancelSeamRenderer()
      targetAmbientPhaseRef.current = null

      ambientLoopsRef.current.forEach((audio, phase) => {
        mixLevelsRef.current.set(phase, 0)
        audio.volume = 0
        audio.pause()
        audio.currentTime = 0
        audio.removeAttribute('src')
        audio.load()
      })
      ambientLoopsRef.current.clear()
      mixLevelsRef.current.clear()
      mediaLoopVisibilityRef.current.clear()
      Array.from(mediaLoopLayersRef.current.keys()).forEach(
        disposeMediaLoopLayer,
      )
      stopOneShots()
      disposeSignalAudio()
    },
    [
      cancelMixAnimation,
      cancelSeamRenderer,
      disposeSignalAudio,
      disposeMediaLoopLayer,
      stopOneShots,
    ],
  )
}
